/* eslint-disable no-underscore-dangle */
(() => {
  // Early return if required libraries are not available
  if (!window.algoliasearch || !window["@algolia/autocomplete-js"]) {
    console.error("Algolia libraries not found");
    return;
  }

  console.log(window.algoliaConfig);

  // Helper function to detect browser locale
  const detectLocale = () => {
    // Check for Shopify locale first
    if (window.Shopify?.locale) {
      return window.Shopify.locale;
    }
    // Fallback to browser locale
    return navigator.language || navigator.userLanguage || "en-US";
  };

  // Helper function to detect currency from Shopify
  const detectCurrency = () =>
    // Check multiple possible Shopify currency locations
    window.Shopify?.currency?.active || window.theme?.currency || window.Currency?.currentCurrency || "USD";

// Helper function to decode HTML entities
const decodeHtmlEntities = (text) => {
  if (!text) return text;
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
};

// Helper function to get localized text
const getLocalizedText = (key, fallback) => {
  let result;

  // First check if search translations are provided
  if (window.searchTranslations?.autocomplete?.[key]) {
    result = window.searchTranslations.autocomplete[key];
  }
  // Then check if custom translations are provided
  else if (window.autocompleteConfig?.labels?.[key]) {
    result = window.autocompleteConfig.labels[key];
  }
  // Then check Shopify theme translations
  else if (window.theme?.strings?.search?.[key]) {
    result = window.theme.strings.search[key];
  }
  // Finally use the fallback
  else {
    result = fallback;
  }

  // Decode HTML entities before returning
  return decodeHtmlEntities(result);
};

  // Build query suggestions index name from main index
  const buildQuerySuggestionsIndexName = (mainIndex) => {
    // Common patterns for query suggestions index names
    if (mainIndex.includes("_products")) {
      return mainIndex.replace("_products", "_products_query_suggestions");
    }
    return `${mainIndex}_query_suggestions`;
  };

  // Configuration
  const config = {
    // Algolia settings
    appId: window.algoliaConfig?.appId,
    apiKey: window.algoliaConfig?.apiKey,
    indexName: window.algoliaConfig?.indexName || "shopify_products",
    querySuggestionsIndexName:
      window.autocompleteConfig?.querySuggestionsIndexName ||
      buildQuerySuggestionsIndexName(window.algoliaConfig?.indexName || "shopify_products"),

    // Fixed category attributes
    categoryAttributes: ["shopify_at_products", "facets", "exact_matches", "tags"],

    // Auto-detect localization for formatting only
    locale: window.autocompleteConfig?.locale || detectLocale(),
    currency: window.autocompleteConfig?.currency || detectCurrency(),

    // Fixed functional settings
    taxRate: 1 + (window.algoliaConfig?.taxRate || 0),
    hitsPerPage: 5,
    suggestionsPerPage: 5,

    // Fixed URL patterns
    searchUrl: "/search",
    productUrlPattern: "/products/{{handle}}",
    collectionUrlPattern: "/collections/{{handle}}",

    // Fixed asset paths
    placeholderImage: window.theme.assets.placeholder,

    // Dynamic text labels only
    labels: {
      placeholder: getLocalizedText(
        "placeholder",
        window.searchTranslations?.autocomplete?.placeholder || "Shop durchsuchen...",
      ),
      products: getLocalizedText("products", window.searchTranslations?.autocomplete?.products || "Produkte"),
      recentSearches: getLocalizedText(
        "recent_searches",
        window.searchTranslations?.autocomplete?.recentSearches || "Letzte Suchen",
      ),
      popularSearches: getLocalizedText(
        "popular_searches",
        window.searchTranslations?.autocomplete?.popularSearches || "Beliebte Suchen",
      ),
      searchIn: getLocalizedText("search_in", window.searchTranslations?.autocomplete?.searchIn || "in"),
      noResultsTitle: getLocalizedText(
        "no_results_title",
        window.searchTranslations?.autocomplete?.noResultsTitle || "KEINE ERGEBNISSE FÜR",
      ),
      noResultsSuggestions: {
        checkSpelling: getLocalizedText(
          "check_spelling",
          window.searchTranslations?.autocomplete?.checkSpelling || "Überprüfen Sie Ihre Rechtschreibung",
        ),
        useFewerKeywords: getLocalizedText(
          "use_fewer_keywords",
          window.searchTranslations?.autocomplete?.useFewerKeywords || "Weniger Schlüsselwörter verwenden",
        ),
        searchLessSpecific: getLocalizedText(
          "search_less_specific",
          window.searchTranslations?.autocomplete?.searchLessSpecific ||
            "Suche nach einem weniger spezifischen Artikel",
        ),
        tryPopularCategories: getLocalizedText(
          "try_popular_categories",
          window.searchTranslations?.autocomplete?.tryPopularCategories ||
            "Versuchen Sie zu navigieren, indem Sie eine der beliebten Kategorien verwenden",
        ),
      },
      popularCategories: getLocalizedText(
        "popular_categories",
        window.searchTranslations?.autocomplete?.popularCategories || "BELIEBTE KATEGORIEN",
      ),
      ...window.autocompleteConfig?.labels,
    },

    // Fixed feature flags
    enableInsights: true,
    enableRedirectPlugin: true,
    enableRecentSearches: true,
    enableQuerySuggestions: true,
  };

  // Destructure required functions from libraries
  const { autocomplete, getAlgoliaResults } = window["@algolia/autocomplete-js"];
  const { createQuerySuggestionsPlugin } = window["@algolia/autocomplete-plugin-query-suggestions"];
  const { createLocalStorageRecentSearchesPlugin } = window["@algolia/autocomplete-plugin-recent-searches"];
  const { createRedirectUrlPlugin } = window["@algolia/autocomplete-plugin-redirect-url"];

  // ——— Custom search client with analytics tagging (PP-756) ———
  const algoliaClient = window.algoliasearch.algoliasearch(
    config.appId || "{{ settings.algolia_app_id }}",
    config.apiKey || "{{ settings.algolia_api_key }}"
  );
  const searchClient = {
    ...algoliaClient,
    search(requests) {
      if (!requests || requests.length === 0) {
        return algoliaClient.search(requests);
      }

      const modifiedRequests = requests.map((request) => {
        const query = request.params?.query || "";

        // Exclude empty/initial autocomplete queries from analytics
        if (query.trim().length === 0) {
          return {
            ...request,
            params: {
              ...request.params,
              analytics: false,
            },
          };
        }

        // Tag typed autocomplete queries for analytics segmentation
        return {
          ...request,
          params: {
            ...request.params,
            analyticsTags: ["typed-search", "autocomplete"],
          },
        };
      });

      return algoliaClient.search(modifiedRequests);
    },
  };  

  /**
   * Helper function to format highlighted text from Algolia
   * @param {string} text - Text with Algolia highlight markers
   * @returns {string} - Text with HTML highlight tags
   */
  const formatHighlightedText = (text) =>
    text.replace(/__aa-highlight__/g, '<strong class="highlight">').replace(/__\/aa-highlight__/g, "</strong>");

  /**
   * Helper function to format URLs with template variables
   * @param {string} template - URL template with {{variable}} placeholders
   * @param {object} data - Data object with values to replace
   * @returns {string} - Formatted URL
   */
  const formatUrl = (template, data) => template.replace(/{{(\w+)}}/g, (match, key) => data[key] || match);

  /**
   * Helper function to handleize strings for URLs
   * Handles different character sets based on locale
   */
  const handleize = (str) => {
    // Get locale-specific character replacements
    const locale = config.locale.toLowerCase();
    let processedStr = str.toLowerCase();

    // Handle German umlauts
    if (locale.startsWith("de")) {
      processedStr = processedStr.replace(/[üöä]/g, (match) => {
        const umlautMap = { ü: "ue", ö: "oe", ä: "ae" };
        return umlautMap[match];
      });
    }

    // Handle French accents
    if (locale.startsWith("fr")) {
      processedStr = processedStr
        .replace(/[àâä]/g, "a")
        .replace(/[éèêë]/g, "e")
        .replace(/[îï]/g, "i")
        .replace(/[ôö]/g, "o")
        .replace(/[ùûü]/g, "u")
        .replace(/ç/g, "c");
    }

    // Common handleization
    return processedStr.replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  };

  // Create plugins array
  const plugins = [];

  // Recent searches plugin
  const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
    key: "navbar",
    transformSource({ source }) {
      return {
        ...source,
        onSelect(params) {
          // Check if there's a redirect URL in the state context
          const redirectData = params.state.context?.redirectUrlPlugin?.data;
          if (redirectData && redirectData.length > 0) {
            const redirectUrl = redirectData[0].urls[0];
            if (redirectUrl) {
              window.location.href = redirectUrl;
              return;
            }
          }

          // Fallback to search page if no redirect URL
          const searchTerm = params.item.label;
          window.location.href = `${config.searchUrl}?q=${encodeURIComponent(searchTerm)}`;
        },
      };
    },
  });
  plugins.push(recentSearchesPlugin);

  // Query suggestions plugin
  const querySuggestionsPlugin = createQuerySuggestionsPlugin({
    searchClient: searchClient,
    indexName: config.querySuggestionsIndexName,
    getSearchParams: () => ({ hitsPerPage: config.suggestionsPerPage }),
    transformSource({ source }) {
      return {
        ...source,
        templates: {
          ...source.templates,
          item({ item, html }) {
            const highlightedQuery = item._highlightResult?.query?.value || item.query;
            const formattedQuery = formatHighlightedText(highlightedQuery);
            const category = item.__autocomplete_qsCategory;

            // Determine the appropriate URL based on whether category exists
            const getUrl = () => {
              if (category) {
                const categoryHandle = handleize(category);
                return formatUrl(config.collectionUrlPattern, { handle: categoryHandle });
              }
              return `${config.searchUrl}?q=${encodeURIComponent(item.query)}`;
            };

            return html`
              <a href="${getUrl()}" class="auto-suggestions">
                <div class="auto-suggestions__svg-holder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M11.6057 6.55286C11.6057 9.34348 9.34348 11.6057 6.55287 11.6057C3.76225 11.6057 1.5 9.34348 1.5 6.55286C1.5 3.76224 3.76224 1.5 6.55286 1.5C9.34348 1.5 11.6057 3.76224 11.6057 6.55286ZM10.6258 11.6866C9.50743 12.575 8.09213 13.1057 6.55287 13.1057C2.93382 13.1057 1.77851e-06 10.1719 1.14574e-06 6.55286C5.12965e-07 2.93382 2.93382 -5.12965e-07 6.55286 -1.14574e-06C10.1719 -1.77851e-06 13.1057 2.93381 13.1057 6.55286C13.1057 8.09219 12.575 9.50755 11.6864 10.6259L15.9452 14.8846L14.8845 15.9453L10.6258 11.6866Z"
                      fill="#838487"
                    ></path>
                  </svg>
                </div>
                <span class="auto-suggestions__text">${html([formattedQuery])}</span>
                ${category
                  ? html`<span
                      class="text text--body-font text--italic"
                      style="--text-color: var(--text-color--secondary)"
                    >
                      - ${config.labels.searchIn} ${category}</span
                    >`
                  : ""}
              </a>
            `;
          },
        },
      };
    },
    categoryAttribute: config.categoryAttributes,
    itemsWithCategories: 2,
    categoriesPerItem: 1,
  });
  plugins.push(querySuggestionsPlugin);

  // Redirect URL plugin
  const redirectUrlPlugin = createRedirectUrlPlugin();
  plugins.push(redirectUrlPlugin);

  // Helper function to create no results template
  const createNoResultsTemplate = (query, html) => html`
    <div class="search-dropdown__no-results">
      <div class="search-dropdown__no-results-content">
        <h2 class="search-dropdown__no-results-title">${config.labels.noResultsTitle} "${query}"</h2>
        <ul class="search-dropdown__suggestions">
          <li>${config.labels.noResultsSuggestions.checkSpelling}</li>
          <li>${config.labels.noResultsSuggestions.useFewerKeywords}</li>
          <li>${config.labels.noResultsSuggestions.searchLessSpecific}</li>
          <li>${config.labels.noResultsSuggestions.tryPopularCategories}</li>
        </ul>
      </div>
      <div class="search-dropdown__categories">
        <h3 class="search-dropdown__categories-title">${config.labels.popularCategories}</h3>
        <div class="search-dropdown__categories-grid">
          ${window.theme?.assets?.autocomplete_collection_image_01 ? html`
            <a href="${window.theme?.assets?.autocomplete_collection_url_01 || '/collections/collection-1'}" class="search-dropdown__category-card">
              <img
                src="${window.theme?.assets?.autocomplete_collection_image_01}"
                alt="${window.theme?.assets?.autocomplete_collection_text_01 || ''}"
              />
              <span>${window.theme?.assets?.autocomplete_collection_text_01 || ''}</span>
            </a>
          ` : ''}
          ${window.theme?.assets?.autocomplete_collection_image_02 ? html`
            <a href="${window.theme?.assets?.autocomplete_collection_url_02 || '/collections/collection-2'}" class="search-dropdown__category-card">
              <img
                src="${window.theme?.assets?.autocomplete_collection_image_02}"
                alt="${window.theme?.assets?.autocomplete_collection_text_02 || ''}"
              />
              <span>${window.theme?.assets?.autocomplete_collection_text_02 || ''}</span>
            </a>
          ` : ''}
          ${window.theme?.assets?.autocomplete_collection_image_03 ? html`
            <a href="${window.theme?.assets?.autocomplete_collection_url_03 || '/collections/collection-3'}" class="search-dropdown__category-card">
              <img
                src="${window.theme?.assets?.autocomplete_collection_image_03}"
                alt="${window.theme?.assets?.autocomplete_collection_text_03 || ''}"
              />
              <span>${window.theme?.assets?.autocomplete_collection_text_03 || ''}</span>
            </a>
          ` : ''}
          ${window.theme?.assets?.autocomplete_collection_image_04 ? html`
            <a href="${window.theme?.assets?.autocomplete_collection_url_04 || '/collections/collection-4'}" class="search-dropdown__category-card">
              <img
                src="${window.theme?.assets?.autocomplete_collection_image_04}"
                alt="${window.theme?.assets?.autocomplete_collection_text_04 || ''}"
              />
              <span>${window.theme?.assets?.autocomplete_collection_text_04 || ''}</span>
            </a>
          ` : ''}
          ${window.theme?.assets?.autocomplete_collection_image_05 ? html`
            <a href="${window.theme?.assets?.autocomplete_collection_url_05 || '/collections/collection-5'}" class="search-dropdown__category-card">
              <img
                src="${window.theme?.assets?.autocomplete_collection_image_05}"
                alt="${window.theme?.assets?.autocomplete_collection_text_05 || ''}"
              />
              <span>${window.theme?.assets?.autocomplete_collection_text_05 || ''}</span>
            </a>
          ` : ''}
          ${window.theme?.assets?.autocomplete_collection_image_06 ? html`
            <a href="${window.theme?.assets?.autocomplete_collection_url_06 || '/collections/collection-6'}" class="search-dropdown__category-card">
              <img
                src="${window.theme?.assets?.autocomplete_collection_image_06}"
                alt="${window.theme?.assets?.autocomplete_collection_text_06 || ''}"
              />
              <span>${window.theme?.assets?.autocomplete_collection_text_06 || ''}</span>
            </a>
          ` : ''}
        </div>
      </div>
    </div>
  `;

  // Create product template renderer
  const createProductTemplate = ({ item, html }) => {
    const highlightedTitle = item._highlightResult?.title?.value || item.title;
    const formattedTitle = formatHighlightedText(highlightedTitle);
    const imageUrl = item.image || config.placeholderImage;
    const imageSrcset = item.image_srcset || "";
    const { price, compare_at_price: compareAtPrice } = item;

    const formatPrice = (amount) => {
      try {
        return new Intl.NumberFormat(config.locale, {
          style: "currency",
          currency: config.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (e) {
        // Fallback formatting if Intl fails
        return `${config.currency} ${amount.toFixed(2)}`;
      }
    };

    const productUrl = formatUrl(config.productUrlPattern, { handle: item.handle });
    const productUrlWithQuery = `${productUrl}?queryID=${item.__autocomplete_queryID}`;

    return html`
      <a
        href="${productUrlWithQuery}"
        product-id="${item.objectID}"
        data-product-handle="${item.handle}"
        class="apc order-1"
      >
        <div class="apc__image">
          <div class="image contain">
            <img
              src="${imageUrl}"
              alt="${item.title}"
              srcset="${imageSrcset}"
              width="500"
              height="218"
              class="image__img contain"
              onerror="this.src='${config.placeholderImage}'"
            />
          </div>
        </div>
        <div class="apc_info">
          <div class="apc__category">${item.vendor}</div>
          <span class="apc__title">${html([formattedTitle])}</span>
          <price-element class="" tax="${config.taxRate}">
            ${compareAtPrice && compareAtPrice > price
              ? html`
                  <span class="price apc__price">
                    <span class="price__current-price">${formatPrice(price)}</span>
                    <span class="price__strike-price">${formatPrice(compareAtPrice)}</span>
                  </span>
                `
              : html`
                  <span class="price apc__price">
                    <span class="price__standart-price">${formatPrice(price)}</span>
                  </span>
                `}
          </price-element>
        </div>
      </a>
    `;
  };

  // Initialize autocomplete
  autocomplete({
    container: "#autocomplete",
    placeholder: config.labels.placeholder,
    openOnFocus: true,
    insights: config.enableInsights,
    plugins,
    onSubmit({ state }) {
      const searchTerm = state.query.trim();
      if (searchTerm) {
        window.location.href = `${config.searchUrl}?q=${encodeURIComponent(searchTerm)}`;
      }
      if (window.ProductSearch?.setInstantSearchUiState) {
        window.ProductSearch.setInstantSearchUiState({ query: state.query });
      }
    },
    onReset() {
      if (window.ProductSearch?.setInstantSearchUiState) {
        window.ProductSearch.setInstantSearchUiState({ query: "" });
      }
    },
    onStateChange({ prevState, state }) {
      if (prevState.query !== state.query && window.ProductSearch?.setInstantSearchUiState) {
        window.ProductSearch.setInstantSearchUiState({ query: state.query });
      }

      /*  // Handle panel positioning for sticky header
      if (state.isOpen) {
        setTimeout(updatePanelPosition, 0);
      } */

      // Handle overlay for detached mode
      const overlay = document.querySelector(".aa-DetachedOverlay");

      if (state.isOpen) {
        // Show overlay when autocomplete is open
        if (!overlay) {
          const newOverlay = document.createElement("div");
          newOverlay.className = "aa-DetachedOverlay";
          newOverlay.style.cssText = `
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            bottom: 0 !important;
            background-color: rgba(0, 0, 0, 0.4) !important;
            z-index: 9 !important;
          `;
          newOverlay.addEventListener("click", () => {
            // Clear the input to close autocomplete
            const input = document.querySelector("#autocomplete input");
            if (input) {
              input.value = "";
              input.blur();
            }
          });
          document.body.appendChild(newOverlay);
        }
      } else if (overlay) {
        // Hide overlay when autocomplete is closed
        overlay.remove();
      }
    },
    detachedMediaQuery: "none",
    getSources({ query }) {
      return [
        {
          sourceId: "products",
          getItems() {
            return getAlgoliaResults({
              searchClient: searchClient,
              queries: [
                {
                  indexName: config.indexName,
                  query,
                  params: {
                    hitsPerPage: config.hitsPerPage,
                  },
                },
              ],
            });
          },
          getItemInputValue({ item }) {
            return item.title;
          },
          templates: {
            item: createProductTemplate,
            header({ html }) {
              return html`<span class="search-dropdown__heading">${config.labels.products}</span>`;
            },
            noResults({ html }) {
              return createNoResultsTemplate(query, html);
            },
          },
        },
      ];
    },
    render(context, root) {
      const { elements, render, html, state } = context;
      const {
        querySuggestionsPlugin: querySuggestionsElement,
        products,
        recentSearchesPlugin: recentSearchesElement,
      } = elements;

      if (state.query) {
        // Check if there are any results in the state collections
        const hasProducts = state.collections.some(
          (collection) => collection.source.sourceId === "products" && collection.items.length > 0,
        );

        const hasSuggestions = state.collections.some(
          (collection) => collection.source.sourceId === "querySuggestionsPlugin" && collection.items.length > 0,
        );

        if (!hasProducts && !hasSuggestions) {
          render(createNoResultsTemplate(state.query, html), root);
          return;
        }

        render(
          html`
            <div class="search-dropdown__left-side">${querySuggestionsElement}</div>
            <div class="search-dropdown__right-side">${products}</div>
          `,
          root,
        );
        return;
      }

      if (recentSearchesElement) {
        render(
          html`
            <div class="search-dropdown__left-side">
              <span class="search-dropdown__heading">${config.labels.recentSearches}</span>
              ${recentSearchesElement}
            </div>
            <div class="search-dropdown__right-side">
              <span class="search-dropdown__heading">${config.labels.popularSearches}</span>
              ${querySuggestionsElement}
            </div>
          `,
          root,
        );
        return;
      }

      render(html` <div class="search-dropdown__left-side">${querySuggestionsElement}</div> `, root);
    },
  });
})();